import type { UserContext } from "@schoolmag/contracts";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./auth";
import { AppShell } from "./components/AppShell";
import { Loading } from "./components/Loading";
import { api } from "./lib/api";
import { AuthPage } from "./pages/AuthPage";
import { DashboardPage } from "./pages/DashboardPage";
import { OnboardingPage } from "./pages/OnboardingPage";
import { PublicationDetailPage } from "./pages/PublicationDetailPage";
import { PublicationListPage } from "./pages/PublicationListPage";
import { PublicationWizardPage } from "./pages/PublicationWizardPage";
import { PageEditorPage } from "./pages/PageEditorPage";
import { MediaPage } from "./pages/MediaPage";
import { ContributorManagementPage } from "./pages/ContributorManagementPage";
import { ContributorPortalPage } from "./pages/ContributorPortalPage";
import { ContributorAcceptPage } from "./pages/ContributorAcceptPage";
import { BrandPage } from "./pages/BrandPage";
import { SettingsPage } from "./pages/SettingsPage";

function RequireSession({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Loading />;
  if (!session) return <Navigate to="/auth" replace state={{ from: location }} />;
  return children;
}

function OnboardingGate({ children }: { children: React.ReactNode }) {
  const context = useQuery({ queryKey: ["context"], queryFn: () => api<UserContext>("/api/me/context") });
  if (context.isLoading) return <Loading />;
  if (context.data?.schools.length === 0) return <Navigate to="/onboarding" replace />;
  return children;
}

export function App() {
  return <Routes>
    <Route path="/auth" element={<AuthPage />} />
    <Route path="/onboarding" element={<RequireSession><OnboardingPage /></RequireSession>} />
    <Route path="/contribute/accept" element={<RequireSession><ContributorAcceptPage /></RequireSession>} />
    <Route path="/contribute" element={<RequireSession><ContributorPortalPage /></RequireSession>} />
    <Route path="/app" element={<RequireSession><OnboardingGate><AppShell /></OnboardingGate></RequireSession>}>
      <Route index element={<DashboardPage />} />
      <Route path="publications" element={<PublicationListPage />} />
      <Route path="publications/new" element={<PublicationWizardPage />} />
      <Route path="publications/:publicationId" element={<PublicationDetailPage />} />
      <Route path="publications/:publicationId/pages/:pageId" element={<PageEditorPage />} />
      <Route path="media" element={<MediaPage />} />
      <Route path="contributors" element={<ContributorManagementPage />} />
      <Route path="brand" element={<BrandPage />} />
      <Route path="settings" element={<SettingsPage />} />
    </Route>
    <Route path="*" element={<Navigate to="/app" replace />} />
  </Routes>;
}
